import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { X } from 'lucide-react';
import { normalizeLanguageCode, type SupportedLanguage } from '@/lib/languageUtils';
import { stripLangPrefix, buildLangPath, LANG_PREFIXES } from '@/components/LangWrapper';
import { isNonTranslatablePath } from '@/lib/nonTranslatablePaths';
import { useLanguage } from '@/hooks/useLanguage';
import { STORAGE_KEYS } from '@/constants/storageKeys';

// Soft language suggestion banner. Triggered when the user's browser
// preference (navigator.languages) doesn't include the URL's locale and they
// haven't already chosen one. Never auto-redirects — only suggests. Choice
// is preserved as a dismiss timestamp (90 days) or by changing language.
//
// Follows Google's hreflang guidance: don't override the user's URL, just
// inform. Apple/Stripe/Airbnb use this pattern. The alternative — Accept-
// Language server-side 302 — breaks CDN caching and confuses Googlebot
// (always en-US), which we explicitly do not want.

const STORAGE_DISMISSED = 'lang-banner-dismissed';
const DISMISS_TTL_DAYS = 90;
const SHOW_DELAY_MS = 1200;

// App/protected routes — never show banner here. Mirrors LangWrapper's
// LANG_SKIP_PREFIXES; checked AFTER stripping any /:lang prefix.
// '/p/' = white-label client portal: our banner must not overlay the
// installer's branding, and /{lang}/p/{token} routes don't exist (the
// accept click 404'd the client's proposal link).
// '/demo' = public full-viewport designer shell (h-screen): this in-flow
// banner made the document banner-height + 100vh, pushing the bottom of the
// designer below the fold (desktop sidebar hint clipped; on mobile the
// banner sat buried behind the designer top bar). ('/try' was the same
// shell for the now-retired anonymous sandbox — route gone, entry removed.)
// Routes with no translated variant now come from the one shared list
// (`nonTranslatablePaths`), so this banner can never offer a language that has
// no page behind it. Its own private copy had drifted: it was missing `/nvr/`,
// so every recorder page offered "view this page in Polish" and the accept
// click called buildLangPath into a URL that does not exist. Only '/demo'
// stays local — that entry is about this banner's layout, not about routing.
const SKIP_PATHS = ['/demo'];

type Message = { prompt: string; accept: string; dismissAria: string; flag: string };

// Native-language prompts. Each entry stays in the SUGGESTED language so the
// user can read it even if the current page is in a language they don't know.
const MESSAGES: Record<SupportedLanguage, Message> = {
  en:      { prompt: 'View this page in English?',                       accept: 'View in English',    dismissAria: 'Dismiss',            flag: 'gb' },
  pl:      { prompt: 'Wyświetlić tę stronę po polsku?',                   accept: 'Po polsku',          dismissAria: 'Zamknij',            flag: 'pl' },
  de:      { prompt: 'Diese Seite auf Deutsch anzeigen?',                 accept: 'Auf Deutsch',        dismissAria: 'Schließen',          flag: 'de' },
  es:      { prompt: '¿Ver esta página en español?',                      accept: 'En español',         dismissAria: 'Cerrar',             flag: 'es' },
  fr:      { prompt: 'Voir cette page en français ?',                     accept: 'En français',        dismissAria: 'Fermer',             flag: 'fr' },
  it:      { prompt: 'Vedere questa pagina in italiano?',                 accept: 'In italiano',        dismissAria: 'Chiudi',             flag: 'it' },
  'pt-br': { prompt: 'Ver esta página em português?',                     accept: 'Em português',       dismissAria: 'Fechar',             flag: 'br' },
  'pt-pt': { prompt: 'Ver esta página em português?',                     accept: 'Em português',       dismissAria: 'Fechar',             flag: 'pt' },
  nl:      { prompt: 'Deze pagina in het Nederlands bekijken?',           accept: 'In het Nederlands',  dismissAria: 'Sluiten',            flag: 'nl' },
  cs:      { prompt: 'Zobrazit tuto stránku v češtině?',                  accept: 'V češtině',          dismissAria: 'Zavřít',             flag: 'cz' },
  uk:      { prompt: 'Переглянути цю сторінку українською?',              accept: 'Українською',        dismissAria: 'Закрити',            flag: 'ua' },
  hu:      { prompt: 'Megtekinti ezt az oldalt magyarul?',                accept: 'Magyarul',           dismissAria: 'Bezárás',            flag: 'hu' },
  lt:      { prompt: 'Peržiūrėti šį puslapį lietuviškai?',                accept: 'Lietuviškai',        dismissAria: 'Uždaryti',           flag: 'lt' },
  ro:      { prompt: 'Vedeți această pagină în română?',                  accept: 'În română',          dismissAria: 'Închideți',          flag: 'ro' },
  tr:      { prompt: 'Bu sayfayı Türkçe görüntülemek ister misiniz?',     accept: 'Türkçe',             dismissAria: 'Kapat',              flag: 'tr' },
  ar:      { prompt: 'عرض هذه الصفحة بالعربية؟',                          accept: 'بالعربية',           dismissAria: 'إغلاق',              flag: 'sa' },
  hi:      { prompt: 'इस पृष्ठ को हिन्दी में देखें?',                       accept: 'हिन्दी में',         dismissAria: 'बंद करें',           flag: 'in' },
  id:      { prompt: 'Lihat halaman ini dalam Bahasa Indonesia?',         accept: 'Bahasa Indonesia',   dismissAria: 'Tutup',              flag: 'id' },
  fil:     { prompt: 'Tingnan ang pahinang ito sa Tagalog?',              accept: 'Sa Tagalog',         dismissAria: 'Isara',              flag: 'ph' },
  vi:      { prompt: 'Xem trang này bằng tiếng Việt?',                    accept: 'Tiếng Việt',         dismissAria: 'Đóng',               flag: 'vn' },
  th:      { prompt: 'ดูหน้านี้เป็นภาษาไทย?',                              accept: 'ภาษาไทย',            dismissAria: 'ปิด',                flag: 'th' },
  ja:      { prompt: 'このページを日本語で表示しますか？',                  accept: '日本語で見る',       dismissAria: '閉じる',             flag: 'jp' },
  ko:      { prompt: '이 페이지를 한국어로 보시겠습니까?',                  accept: '한국어로 보기',      dismissAria: '닫기',               flag: 'kr' },
  sv:      { prompt: 'Visa den här sidan på svenska?',                    accept: 'På svenska',         dismissAria: 'Stäng',              flag: 'se' },
  bg:      { prompt: 'Да видите тази страница на български?', accept: 'На български', dismissAria: 'Затваряне', flag: 'bg' },
  da:      { prompt: 'Vil du se denne side på dansk?', accept: 'På dansk', dismissAria: 'Luk', flag: 'dk' },
  el:      { prompt: 'Προβολή αυτής της σελίδας στα ελληνικά;', accept: 'Στα ελληνικά', dismissAria: 'Κλείσιμο', flag: 'gr' },
  et:      { prompt: 'Kas soovite seda lehte eesti keeles?', accept: 'Eesti keeles', dismissAria: 'Sulge', flag: 'ee' },
  fi:      { prompt: 'Näytetäänkö tämä sivu suomeksi?', accept: 'Suomeksi', dismissAria: 'Sulje', flag: 'fi' },
  hr:      { prompt: 'Želite li vidjeti ovu stranicu na hrvatskom?', accept: 'Na hrvatskom', dismissAria: 'Zatvori', flag: 'hr' },
  lv:      { prompt: 'Vai skatīt šo lapu latviski?', accept: 'Latviski', dismissAria: 'Aizvērt', flag: 'lv' },
  sk:      { prompt: 'Zobraziť túto stránku v slovenčine?', accept: 'V slovenčine', dismissAria: 'Zavrieť', flag: 'sk' },
  sl:      { prompt: 'Želite to stran videti v slovenščini?', accept: 'V slovenščini', dismissAria: 'Zapri', flag: 'si' },
};

function getUrlLang(pathname: string): SupportedLanguage {
  const first = pathname.split('/').filter(Boolean)[0];
  return (first && (LANG_PREFIXES as readonly string[]).includes(first))
    ? (first as SupportedLanguage)
    : 'en';
}

// normalizeLanguageCode handles pt-* variants but returns null for region-
// suffixed codes like en-US / de-AT. Add a base-code fallback so the banner
// fires for real-world Accept-Language strings.
function detectSupported(raw: string): SupportedLanguage | null {
  const direct = normalizeLanguageCode(raw);
  if (direct) return direct;
  const base = raw.toLowerCase().split('-')[0];
  return normalizeLanguageCode(base);
}

function pickSuggestion(urlLang: SupportedLanguage): SupportedLanguage | null {
  if (typeof navigator === 'undefined') return null;
  const list = navigator.languages?.length ? Array.from(navigator.languages) : [navigator.language];
  const supported: SupportedLanguage[] = [];
  for (const raw of list) {
    const code = detectSupported(raw);
    if (code && !supported.includes(code)) supported.push(code);
  }
  if (supported.length === 0) return null;
  // User's browser already lists the current URL language anywhere in its
  // preference order → they speak it, no need to suggest anything.
  if (supported.includes(urlLang)) return null;
  return supported[0];
}

function isDismissedRecently(): boolean {
  try {
    const raw = localStorage.getItem(STORAGE_DISMISSED);
    if (!raw) return false;
    const ts = Number(raw);
    if (!Number.isFinite(ts)) return false;
    return (Date.now() - ts) < DISMISS_TTL_DAYS * 24 * 60 * 60 * 1000;
  } catch {
    return false;
  }
}

function hasExplicitLangPreference(): boolean {
  try {
    return !!localStorage.getItem(STORAGE_KEYS.LANGUAGE);
  } catch {
    return false;
  }
}

function isAppRoute(pathname: string): boolean {
  const clean = stripLangPrefix(pathname);
  return isNonTranslatablePath(clean)
    || SKIP_PATHS.some(p => clean === p || clean.startsWith(p));
}

export const LanguageSuggestionBanner = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { changeLanguage } = useLanguage();
  const [suggested, setSuggested] = useState<SupportedLanguage | null>(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (isAppRoute(location.pathname)) return;
    if (hasExplicitLangPreference()) return;
    if (isDismissedRecently()) return;

    const urlLang = getUrlLang(location.pathname);
    const candidate = pickSuggestion(urlLang);
    if (!candidate) return;

    const timer = setTimeout(() => setSuggested(candidate), SHOW_DELAY_MS);
    return () => clearTimeout(timer);
  }, [location.pathname]);

  const dismiss = () => {
    try {
      localStorage.setItem(STORAGE_DISMISSED, String(Date.now()));
    } catch { /* ignore */ }
    setSuggested(null);
  };

  const accept = async () => {
    if (!suggested) return;
    await changeLanguage(suggested);
    const basePath = stripLangPrefix(location.pathname);
    const targetPath = buildLangPath(basePath, suggested);
    setSuggested(null);
    if (location.pathname !== targetPath) navigate(targetPath);
  };

  if (!suggested) return null;
  const msg = MESSAGES[suggested];
  if (!msg) return null;

  const isRTL = suggested === 'ar';

  return (
    <div
      role="region"
      aria-label={msg.prompt}
      dir={isRTL ? 'rtl' : 'ltr'}
      style={{
        background: 'var(--color-page-bg)',
        borderBottom: '1px solid var(--color-gray-border-strong)',
      }}
    >
      <div className="pelco-container flex items-center gap-3 md:gap-5 py-2.5 md:py-3 px-4 md:px-6">
        <span
          className={`fi fi-${msg.flag}`}
          aria-hidden="true"
          style={{
            display: 'inline-block',
            width: '1.5em',
            height: '1.1em',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            flexShrink: 0,
          }}
        />
        <p
          className="flex-1 text-[13px] md:text-[14px] m-0"
          style={{
            fontFamily: 'var(--font-body)',
            color: 'var(--color-gray-dark)',
            lineHeight: 1.4,
          }}
        >
          {msg.prompt}
        </p>
        <button
          type="button"
          onClick={accept}
          className="uppercase whitespace-nowrap"
          style={{
            fontFamily: 'var(--font-mono)',
            fontSize: '11px',
            fontWeight: 600,
            letterSpacing: '0.14em',
            background: 'var(--color-gray-dark)',
            color: 'var(--color-page-bg)',
            padding: '0.5rem 1rem',
            border: 0,
            cursor: 'pointer',
          }}
        >
          {msg.accept}
        </button>
        <button
          type="button"
          onClick={dismiss}
          aria-label={msg.dismissAria}
          className="shrink-0 p-1"
          style={{
            background: 'transparent',
            border: 0,
            color: 'var(--color-gray-muted)',
            cursor: 'pointer',
          }}
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default LanguageSuggestionBanner;
